import { UsersService } from 'store/services/users.service';
import type { AnyAction, ThunkAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/index';
import type { Note } from 'components/Admin/types';
import type { InvitedUser } from 'components/Admin/organisms/Accounts/molecules/NewStaffAccount/useNewStaffAccount';
import type { AcceptInvitationRequest } from 'components/Admin/organisms/Accounts/molecules/NewStaffAccount/useNewStaffAccount';
import type { StaffPermissions } from 'components/Admin/organisms/Accounts/types';
import type { User } from 'types/users';
import { generalSliceActions } from 'store/slices/general/config';
import type { FilesDto } from 'store/slices/cars/types';
import type { NewCommentRequestPayload } from 'store/services/comments.service';
import { CommentsService } from 'store/services/comments.service';
import {
  rentDetailsActions,
  rentDetailsSelectors,
} from 'store/slices/rentDetails/config';
import type { LanguageType } from 'common/constants';
import { objectIsChanged } from 'common/helpers/objectIsChanged';
import { removeKeysFromObject } from 'common/helpers/removeKeysFromObject';
import { RentalDrawerSteps } from 'components/Reservation/types/types';
import { ordersActions } from 'store/slices/orders/config';
import { subscriptionsActions } from 'store/slices/subscriptions/config';
import { carsActions } from 'store/slices/cars/config';
import { discountsActions } from 'store/slices/discounts/config';
import { upcomingActionsMethods } from 'store/slices/upcoming-actions/config';
import { SubscriptionsService } from 'store/services/subscriptions.service';
import type { GetUsersQueryParams } from './users.slice';
import { usersActions, usersSelectors } from './config';

export const getAllUsers =
  (
    params: Partial<GetUsersQueryParams>,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch: any) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.getAllUsers(params);
      dispatch(usersActions.setAllUsers(response));
    } catch (e: any) {
      throw new Error(e);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const archiveCustomers =
  (ids: Array<string>): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const allCustomers = usersSelectors.selectAllUsers(getState());
      const validCustomers = ids.filter(
        (id) => !allCustomers.find((item) => item.id === id)?.isArchived,
      );

      await UsersService.archiveUsers(validCustomers);
      dispatch(usersActions.applyArchivedUsers(validCustomers));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const removeCustomers =
  (ids: Array<string>): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      await UsersService.removeCustomers(ids);
      dispatch(usersActions.deleteUsers(ids));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const getUserNotes =
  (): ThunkAction<void, RootState, unknown, AnyAction> => async (dispatch) => {
    try {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          general: true,
        }),
      );
      const response = await UsersService.getUserNotes();
      dispatch(usersActions.setUserNotes(response));
    } catch (e: any) {
      throw new Error(e);
    } finally {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          general: false,
          specificNote: null,
        }),
      );
    }
  };

export const createNewNote =
  (note: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          general: true,
        }),
      );
      const response = await UsersService.createNote({
        content: note,
      });
      dispatch(usersActions.setNewNote(response));
    } catch (e: any) {
      throw new Error(e);
    } finally {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          general: false,
          specificNote: null,
        }),
      );
    }
  };

export const deleteNoteThunk =
  (noteId: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          specificNote: noteId,
        }),
      );
      await UsersService.deleteNote(noteId);
      dispatch(usersActions.deleteNote(noteId));
    } catch (e: any) {
      throw new Error(e);
    } finally {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          general: false,
          specificNote: null,
        }),
      );
    }
  };

export const updateNoteThunk =
  (updatedNote: Note): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          specificNote: updatedNote.id,
        }),
      );
      const response = await UsersService.updateNote(updatedNote);
      dispatch(usersActions.updateNote(response));
    } catch (e: any) {
      throw new Error(e);
    } finally {
      dispatch(
        usersActions.setUserNotesPendingStatus({
          general: false,
          specificNote: null,
        }),
      );
    }
  };

export const getAllOwnersAndStaffAccounts =
  (): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const allAccounts = usersSelectors.selectAllUsers(getState());
      const [allOwners, allStaff] = await Promise.all([
        UsersService.getAllOwnerAccounts(),
        UsersService.getAllStaffAccounts(),
      ]);

      dispatch(
        usersActions.setAllUsers(
          allAccounts.concat([...allOwners, ...allStaff]),
        ),
      );
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const inviteUserThunk =
  (
    payload: InvitedUser & { permissions: StaffPermissions },
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const allAccounts = usersSelectors.selectAllUsers(getState());
      const result = await UsersService.inviteUser(payload);
      dispatch(usersActions.setAllUsers(allAccounts.concat(result)));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const acceptInvitation =
  (
    payload: AcceptInvitationRequest,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const result = await UsersService.acceptInvitation(payload);
      dispatch(usersActions.updateUser(result.user));
      dispatch(usersActions.setProfile(result.user));
      dispatch(usersActions.setAccessToken(result.accessToken));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const deleteUser =
  (id: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      await UsersService.deleteUser(id);
      dispatch(usersActions.removeUser(id));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };
export const getUserById =
  (id: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.getUserById(id);
      dispatch(usersActions.setSelectedUser(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const getCustomerInformation =
  (id: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    // customer
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.getCustomerById(id);
      dispatch(usersActions.setSelectedUser(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const updateUserThunk =
  (
    payload: Partial<User>,
    cb?: (user: User) => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const profile = usersSelectors.selectProfile(getState());
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.updateUser(payload);
      const isSameProfile = profile?.id === response.id;
      if (isSameProfile) {
        dispatch(usersActions.setProfile(response));
      }
      dispatch(generalSliceActions.setTemporaryLanguage(response.language));
      dispatch(usersActions.updateUser(response));
      if (isSameProfile) {
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'success',
            message: 'Your profile has been successfully updated.',
          }),
        );
      }

      if (cb) {
        cb(response);
      }
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(rentDetailsActions.setIsOrderCreationInProcess(false));
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const getMe =
  (): ThunkAction<void, RootState, unknown, AnyAction> => async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.getMe();
      dispatch(usersActions.setProfile(response));
      if (response.language) {
        dispatch(generalSliceActions.setTemporaryLanguage(response.language));
      }
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const login =
  (
    payload: {
      email: string;
      password: string;
    },
    cb?: () => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.login(payload);
      dispatch(usersActions.setAccessToken(response.accessToken));
      dispatch(usersActions.setProfile(response.user));
      dispatch(rentDetailsActions.resetRentalDrawerErrorFields());
      dispatch(
        generalSliceActions.setTemporaryLanguage(
          response.user.language as LanguageType,
        ),
      );

      dispatch(
        generalSliceActions.setAppMessage({
          type: 'success',
          message: 'Login successfull',
        }),
      );

      if (cb) {
        cb();
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const updateProfilePassword =
  (credentials: {
    currentPassword: string;
    confirmNewPassword: string;
    newPassword: string;
  }): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setPasswordPendingStatus(true));
      await UsersService.updatePassword(credentials);
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'success',
          message: 'Your password successfully changed',
        }),
      );
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: 'Current password is not valid',
        }),
      );
    } finally {
      dispatch(usersActions.setPasswordPendingStatus(false));
    }
  };

export const resetPassword =
  (
    email: string,
    cb: () => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setUsersPendingStatus(true));
      await UsersService.resetPassword(email);
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'success',
          message:
            'A verification link will be sent to your email address if an account exists.',
        }),
      );
      if (cb) {
        cb();
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const updateUserPassword =
  (
    credentials: {
      confirmNewPassword: string;
      newPassword: string;
      token: string;
    },
    cb: () => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      if (!credentials.token) {
        return;
      }
      dispatch(usersActions.setUsersPendingStatus(true));
      const response = await UsersService.setNewPassword(credentials);
      dispatch(usersActions.setAccessToken(response.accessToken));
      dispatch(usersActions.setProfile(response.user));
      if (cb) {
        cb();
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(usersActions.setUsersPendingStatus(false));
    }
  };

export const downloadFile =
  (file: Partial<FilesDto>): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      await UsersService.getFile(file);
    } catch (err) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: 'Failed to download image',
        }),
      );
    }
  };

export const checkUserIsExistAndRegisterUser =
  (
    payload: User,
    cb?: () => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(rentDetailsActions.setIsAccountCreationStepPending(true));
      const user = await UsersService.getUserByEmail(payload.email);
      if (user.id) {
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'error',
            message: 'User with this email already exist',
          }),
        );
        dispatch(rentDetailsActions.setIsAccountCreationStepPending(false));
        return;
      }

      const response = await UsersService.registerUser(payload);
      dispatch(usersActions.setProfile(response.user));
      dispatch(usersActions.setAccessToken(response.tokens.accessToken));
      dispatch(rentDetailsActions.resetRentalDrawerErrorFields());
      if (cb) {
        cb();
      }
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(rentDetailsActions.setIsAccountCreationStepPending(false));
    }
  };

export const createCommentAboutUser =
  (
    payload: NewCommentRequestPayload,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const response = await CommentsService.createCommentAboutUser(payload);
      console.log(response);
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const sendVerificationEmail =
  (cb: () => void): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const profile = usersSelectors.selectProfile(getState());
      if (!profile || !profile.email) {
        return;
      }
      await UsersService.sendVerificationEmail();
      cb && cb();
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const verifyAccount =
  (
    verificationToken: string,
    message: string,
    cb: () => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(generalSliceActions.setAppIsLoading(true));
      const response = await UsersService.verifyAccount(verificationToken);
      dispatch(usersActions.setProfile(response));
      dispatch(usersActions.setAccessToken(verificationToken));
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'success',
          message,
        }),
      );
      cb();
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(generalSliceActions.setAppIsLoading(false));
    }
  };

export const createUserWithoutPassword =
  (payload: Partial<User>): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setIsSecondaryDriverCreating(true));
      if (!payload.email) {
        return;
      }
      const user = await UsersService.getUserByEmail(payload.email);
      if (user.id) {
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'error',
            message: 'User with this email already exist',
          }),
        );
        return;
      }
      const response = await UsersService.createUserWithoutPassword(payload);
      dispatch(usersActions.changeSecondaryDriver(response));
      dispatch(usersActions.setNewUser(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(usersActions.setIsSecondaryDriverCreating(false));
    }
  };
export const validateOrderUser =
  (): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(rentDetailsActions.setIsAccountCreationStepPending(true));
      const step = rentDetailsSelectors.selectRentalDrawerStep(getState());
      const currentLinkedList = RentalDrawerSteps[step];
      const profile = usersSelectors.selectProfile(getState());
      const draftUser = usersSelectors.selectNewDraftUser(getState());

      const isChanged = objectIsChanged(profile, draftUser);

      if (isChanged) {
        const response = await UsersService.updateUser({
          ...removeKeysFromObject(draftUser as User, [
            'password',
            'confirmPassword',
          ]),
          id: profile?.id,
        });
        if (profile?.id === response.id) {
          dispatch(usersActions.setProfile(response));
        }
        dispatch(
          generalSliceActions.setTemporaryLanguage(
            response.language as LanguageType,
          ),
        );
        dispatch(usersActions.updateUser(response));
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'success',
            message: 'Your profile has been successfully updated.',
          }),
        );
      }
      if (currentLinkedList.next) {
        dispatch(rentDetailsActions.setDrawerStep(currentLinkedList.next));
      }
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(rentDetailsActions.setIsAccountCreationStepPending(false));
    }
  };

export const socialLogin =
  (
    accessToken: string,
    callback?: () => void,
  ): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(usersActions.setIsSocialLoginPending(true));
      const response = await UsersService.socialLogin(accessToken);
      dispatch(usersActions.setAccessToken(response.accessToken));
      dispatch(usersActions.setProfile(response.user));
      dispatch(rentDetailsActions.resetRentalDrawerErrorFields());
      dispatch(
        generalSliceActions.setTemporaryLanguage(
          response.user.language as LanguageType,
        ),
      );

      dispatch(usersActions.resetDraftUser());
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'success',
          message: 'Login successfull',
        }),
      );
      if (callback) {
        callback();
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(usersActions.setIsSocialLoginPending(false));
    }
  };

export const logout =
  (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) => {
    localStorage.removeItem('token');
    dispatch(ordersActions.resetStore());
    dispatch(subscriptionsActions.resetStore());
    dispatch(usersActions.resetStore());
    dispatch(carsActions.resetStore());
    dispatch(discountsActions.resetStore());
    dispatch(rentDetailsActions.resetStore());
    dispatch(upcomingActionsMethods.resetStore());
  };

export const stopSubscription =
  (id: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(subscriptionsActions.setSubscriptionsPendingStatus(true));
      const response = await SubscriptionsService.stopSubscription(id);
      dispatch(usersActions.updateProfileSubscription(response));
      dispatch(subscriptionsActions.updateSubscription(response));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(subscriptionsActions.setSubscriptionsPendingStatus(false));
    }
  };
